import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined"
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined"
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined"
import Alert from "@mui/material/Alert"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Stack from "@mui/material/Stack"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { alpha } from "@mui/material/styles"
import { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import Row from "../../../common/components/Row"
import { selectProfile } from "../../users/reducer"
import {
  ConciergeHero,
  buildConciergeInterestMailto,
} from "./ConciergeFeatureHighlight"
import ConciergeInboxTemplateApprovalsSection from "./ConciergeInboxTemplateApprovalsSection"

function formatAddress(concierge) {
  if (concierge.address) {
    return concierge.address
  }
  const city = (concierge.city || "").trim()
  const state = (concierge.state || "").trim()
  if (city && state) {
    return `${city}, ${state}`
  }
  return city || state || null
}

function ContactAction({ icon, label, href, onClick, value }) {
  if (!value) {
    return null
  }
  const isExternalLink = Boolean(href) && !onClick
  return (
    <Tooltip title={label}>
      <IconButton
        component={isExternalLink ? "a" : "button"}
        type={isExternalLink ? undefined : "button"}
        href={isExternalLink ? href : undefined}
        onClick={onClick}
        size="small"
        aria-label={label}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          "&:hover": {
            borderColor: "secondary.main",
            backgroundColor: (theme) =>
              alpha(theme.palette.secondary.main, 0.08),
          },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  )
}

function ConciergeCard({ concierge, index, onCopyPhone }) {
  const initials =
    concierge.first_name?.[0] ||
    concierge.name?.[0] ||
    concierge.email?.[0]?.toUpperCase() ||
    "?"
  const address = formatAddress(concierge)

  return (
    <Box
      sx={{
        position: "relative",
        p: 2.5,
        borderRadius: "14px",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        boxShadow: "0 8px 20px -16px rgba(15, 23, 42, 0.28)",
        transition: "border-color 160ms ease, box-shadow 160ms ease",
        animation: "conciergeCardIn 360ms ease both",
        animationDelay: `${index * 50}ms`,
        "@keyframes conciergeCardIn": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "&:hover": {
          borderColor: (theme) => alpha(theme.palette.secondary.main, 0.35),
          boxShadow: "0 12px 24px -16px rgba(15, 23, 42, 0.35)",
        },
      }}
    >
      <Row sx={{ gap: 2, alignItems: "flex-start" }}>
        <Avatar
          src={concierge.profile_image || undefined}
          sx={{
            width: 64,
            height: 64,
            fontSize: "1.25rem",
            fontWeight: 700,
            bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
            color: "secondary.dark",
          }}
        >
          {concierge.profile_image ? undefined : initials}
        </Avatar>
        <Column sx={{ gap: 0.75, minWidth: 0, flex: 1 }}>
          <Row
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}
                noWrap
              >
                {concierge.name || "Concierge"}
              </Typography>
              {concierge.job_title ? (
                <Row sx={{ alignItems: "center", gap: 0.5, mt: 0.25 }}>
                  <WorkOutlineOutlinedIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {concierge.job_title}
                  </Typography>
                </Row>
              ) : (
                <Chip
                  size="small"
                  label="SmartSetter Concierge"
                  sx={{ mt: 0.5, height: 22, fontWeight: 600 }}
                />
              )}
            </Box>
            <Row sx={{ gap: 0.75 }}>
              <ContactAction
                icon={<EmailOutlinedIcon fontSize="small" />}
                label={`Email ${concierge.email}`}
                href={concierge.email ? `mailto:${concierge.email}` : undefined}
                value={concierge.email}
              />
              <ContactAction
                icon={<PhoneOutlinedIcon fontSize="small" />}
                label={`Copy ${concierge.phone}`}
                value={concierge.phone}
                onClick={() => onCopyPhone?.(concierge.phone)}
              />
            </Row>
          </Row>

          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {concierge.email && (
              <Row sx={{ alignItems: "center", gap: 1 }}>
                <EmailOutlinedIcon
                  sx={{ fontSize: 18, color: "text.secondary" }}
                />
                <Link
                  href={`mailto:${concierge.email}`}
                  underline="hover"
                  variant="body2"
                  color="text.primary"
                >
                  {concierge.email}
                </Link>
              </Row>
            )}
            {concierge.phone && (
              <Row sx={{ alignItems: "center", gap: 1 }}>
                <PhoneOutlinedIcon
                  sx={{ fontSize: 18, color: "text.secondary" }}
                />
                <Typography variant="body2" color="text.primary">
                  {concierge.phone}
                </Typography>
              </Row>
            )}
            {address && (
              <Row sx={{ alignItems: "center", gap: 1 }}>
                <LocationOnOutlinedIcon
                  sx={{ fontSize: 18, color: "text.secondary" }}
                />
                <Typography variant="body2">{address}</Typography>
              </Row>
            )}
          </Stack>

          {concierge.twilio_voice_phone ? (
            <Alert
              severity="success"
              icon={<PhoneInTalkOutlinedIcon fontSize="inherit" />}
              sx={{ mt: 1.75, py: 0.5 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                CRM calling number for your account
              </Typography>
              <Typography variant="body2">
                {concierge.twilio_voice_phone}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Candidates see this number when your Concierge calls from the
                CRM.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 1.75, py: 0.5 }}>
              <Typography variant="body2">
                No Twilio calling number connected for your account yet. Your
                Concierge can buy one in Settings while working on your account.
              </Typography>
            </Alert>
          )}
        </Column>
      </Row>
    </Box>
  )
}

function EmptyConciergeState({ mailtoHref }) {
  return (
    <Box
      sx={{
        borderRadius: "16px",
        p: { xs: 2.5, sm: 3 },
        border: "1px dashed",
        borderColor: "divider",
        backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.03),
      }}
    >
      <Column sx={{ gap: 2, alignItems: "flex-start", width: "100%" }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            backgroundColor: (theme) =>
              alpha(theme.palette.secondary.main, 0.1),
            color: "secondary.main",
          }}
        >
          <SupportAgentOutlinedIcon />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, letterSpacing: "-0.01em", mb: 0.75 }}
          >
            No Concierge connected yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            Tell support you&apos;re interested and we&apos;ll assign a
            Concierge to your account. You&apos;ll see their photo and contact
            details here once they&apos;re connected.
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.25 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Dedicated help in your CRM
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Clear attribution on activity
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Assigned by the SmartSetter support team
            </Typography>
          </Stack>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          component="a"
          href={mailtoHref}
          startIcon={<SupportAgentOutlinedIcon />}
          sx={{ borderRadius: 2, px: 2.25, fontWeight: 600 }}
        >
          Email support about Concierge
        </Button>
      </Column>
    </Box>
  )
}

export default function ConciergePanel() {
  const axiosClient = useAuthorizedAxiosClient()
  const profile = useSelector(selectProfile)
  const [isLoading, setIsLoading] = useState(true)
  const [payload, setPayload] = useState({
    count: 0,
    concierges: [],
  })
  const {
    isOpen: isSnackbarOpen,
    message: snackbarMessage,
    severity: snackbarSeverity,
    open: openSnackbar,
    openError: openErrorSnackbar,
    close: closeSnackbar,
  } = useCloseableSnackbarState()

  const loadConcierges = useCallback(() => {
    setIsLoading(true)
    axiosClient
      .get("/clients/api/account-concierges/")
      .then(({ data }) => setPayload(data))
      .catch(({ response }) => {
        openErrorSnackbar(
          parseApiErrorMessage(
            response?.data,
            "Could not load connected Concierges"
          )
        )
      })
      .finally(() => setIsLoading(false))
  }, [axiosClient, openErrorSnackbar])

  useEffect(() => {
    loadConcierges()
    // Load once when the Concierge panel mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const mailtoHref = buildConciergeInterestMailto(profile)
  const { count, concierges } = payload

  function handleCopyPhone(phone) {
    if (!phone) {
      return
    }
    navigator.clipboard?.writeText(phone).then(
      () => openSnackbar("Phone number copied"),
      () => openErrorSnackbar("Could not copy phone number")
    )
  }

  return (
    <Column
      sx={{ gap: 3, alignItems: "stretch", width: "100%", maxWidth: "none" }}
    >
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <ConciergeHero connectedCount={isLoading ? 0 : count} />

      <ConciergeInboxTemplateApprovalsSection onStatusMessage={openSnackbar} />

      {!isLoading && count > 0 && (
        <Row
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Your connected Concierges
          </Typography>
          <Chip
            size="small"
            label={`${count} connected`}
            sx={{ fontWeight: 600 }}
          />
        </Row>
      )}

      {!isLoading && count === 0 && (
        <EmptyConciergeState mailtoHref={mailtoHref} />
      )}

      {!isLoading && count > 0 && (
        <Column sx={{ gap: 2 }}>
          {concierges.map((concierge, index) => (
            <ConciergeCard
              key={concierge.id}
              concierge={concierge}
              index={index}
              onCopyPhone={handleCopyPhone}
            />
          ))}
        </Column>
      )}

      <CloseableSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Column>
  )
}
